<template>
  <div class="week-page">
    <div class="week-card">
      <div class="toolbar">
        <div>
          <div class="title">周表管理</div>
          <div class="muted">{{ weekRange }}</div>
        </div>
        <div class="toolbar-actions">
          <el-button icon="el-icon-refresh" :loading="loading" @click="loadStatus">刷新</el-button>
          <el-button type="primary" icon="el-icon-video-play" :loading="running" @click="runAuto">立即检查</el-button>
        </div>
      </div>

      <el-row :gutter="16">
        <el-col :span="8">
          <div class="section">
            <div class="section-title">本周状态</div>
            <div class="status-line">
              <el-tag :type="statusTag(mainRow.status)" size="medium">{{ statusText(mainRow.status) }}</el-tag>
              <span class="status-text">{{ mainRow.message || (status.already_synced ? "本周已有日程数据" : "等待同步") }}</span>
            </div>
            <el-descriptions :column="1" border size="small" class="meta">
              <el-descriptions-item label="动态ID">{{ mainRow.dynamic_id || "-" }}</el-descriptions-item>
              <el-descriptions-item label="同步时间">{{ formatTime(mainRow.synced_at) }}</el-descriptions-item>
              <el-descriptions-item label="更新时间">{{ formatTime(mainRow.updated_at) }}</el-descriptions-item>
            </el-descriptions>
            <a v-if="mainRow.dynamic_url" class="link" :href="mainRow.dynamic_url" target="_blank" rel="noopener noreferrer">打开已同步动态</a>
            <el-alert v-if="status.dynamic_error" class="alert" type="warning" :closable="false" show-icon :title="status.dynamic_error" />
          </div>
        </el-col>

        <el-col :span="16">
          <div class="section">
            <div class="section-head">
              <div>
                <div class="section-title">天选姬近期动态</div>
                <div class="muted">选择一条动态，让 AI 判断是否为周表并写入日程表</div>
              </div>
              <el-button size="small" icon="el-icon-refresh" @click="loadDynamics">重新拉取</el-button>
            </div>
            <el-table :data="dynamics" height="330" border size="small" @current-change="selectDynamic" highlight-current-row>
              <el-table-column label="动态" min-width="260">
                <template slot-scope="scope">
                  <div class="strong">{{ scope.row.content || "无文案" }}</div>
                  <div class="muted mono">{{ scope.row.id }}</div>
                  <a class="mini-link" :href="scope.row.url" target="_blank" rel="noopener noreferrer">{{ scope.row.url }}</a>
                </template>
              </el-table-column>
              <el-table-column label="发布时间" width="150">
                <template slot-scope="scope">{{ formatTime(scope.row.created) }}</template>
              </el-table-column>
              <el-table-column label="判断" width="150">
                <template slot-scope="scope">
                  <el-tag size="mini" :type="scope.row.week_schedule_guess ? 'success' : 'info'">{{ scope.row.week_schedule_guess ? "疑似周表" : "未命中文案" }}</el-tag>
                  <el-tag size="mini" :type="scope.row.in_current_week ? 'primary' : 'warning'">{{ scope.row.in_current_week ? "本周" : "非本周" }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="图片" width="100">
                <template slot-scope="scope">
                  <el-image
                    v-if="scope.row.images && scope.row.images.length"
                    class="thumb"
                    :src="scope.row.images[0]"
                    :preview-src-list="scope.row.images"
                    fit="cover"
                    referrerpolicy="no-referrer"
                  />
                  <span v-else class="muted">无</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="110">
                <template slot-scope="scope">
                  <el-button type="primary" size="mini" :loading="manualRunning && selectedDynamicId === scope.row.id" @click="manualSync(scope.row)">AI执行</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>
      </el-row>

      <el-row :gutter="16" class="bottom-row">
        <el-col :span="11">
          <div class="section">
            <div class="section-title">图片同步记录</div>
            <el-table :data="status.image_rows || []" height="260" border size="small">
              <el-table-column prop="account_name" label="账号" width="130"></el-table-column>
              <el-table-column label="状态" width="110">
                <template slot-scope="scope">
                  <el-tag size="mini" :type="statusTag(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="message" label="信息" min-width="180" show-overflow-tooltip></el-table-column>
              <el-table-column label="更新时间" width="145">
                <template slot-scope="scope">{{ formatTime(scope.row.updated_at) }}</template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>
        <el-col :span="13">
          <div class="section">
            <div class="section-title">本周日程数据</div>
            <el-table :data="status.date_rows || []" height="260" border size="small">
              <el-table-column prop="date_text" label="日期" width="110"></el-table-column>
              <el-table-column prop="weekday" label="星期" width="70"></el-table-column>
              <el-table-column label="状态" width="80">
                <template slot-scope="scope">{{ dateStatus(scope.row.status) }}</template>
              </el-table-column>
              <el-table-column label="类型" width="90">
                <template slot-scope="scope">{{ typeText(scope.row.type) }}</template>
              </el-table-column>
              <el-table-column prop="time" label="时间" width="120"></el-table-column>
              <el-table-column prop="data" label="内容" min-width="160" show-overflow-tooltip></el-table-column>
            </el-table>
          </div>
        </el-col>
      </el-row>

      <el-dialog title="AI 执行结果" :visible.sync="resultVisible" width="760px">
        <pre class="result-json">{{ prettyJson(lastResult) }}</pre>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { post } from "../axios/request.js";
import { formatTime, isSuccess, prettyJson } from "./appAdminUtils.js";

export default {
  name: "appWeekSchedule",
  data() {
    return {
      loading: false,
      running: false,
      manualRunning: false,
      resultVisible: false,
      selectedDynamicId: "",
      status: {
        image_rows: [],
        date_rows: [],
        dynamics: [],
      },
      dynamics: [],
      lastResult: null,
      typeMap: ["杂谈", "歌回", "游戏回", "水友回", "DD歌回", "联动回", "二创回", "其他"],
    };
  },
  computed: {
    mainRow() {
      return this.status.row || {};
    },
    weekRange() {
      if (!this.status.week_start) {
        return "本周周表更新数据";
      }
      return `${this.status.week_start} 至 ${this.status.week_end}`;
    },
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    formatTime,
    prettyJson,
    loadStatus() {
      this.loading = true;
      post("/api/admin/week-schedule-sync/status", { with_dynamics: 1, count: 12 }).then((res) => {
        if (!isSuccess(res)) {
          this.$message.error(res.data.msg || "周表状态获取失败");
          return;
        }
        this.status = res.data.data || {};
        this.dynamics = this.status.dynamics || [];
      }).finally(() => {
        this.loading = false;
      });
    },
    loadDynamics() {
      post("/api/admin/week-schedule-sync/dynamics", { count: 20 }).then((res) => {
        if (isSuccess(res)) {
          this.dynamics = res.data.data || [];
        } else {
          this.$message.error(res.data.msg || "动态拉取失败");
        }
      });
    },
    runAuto() {
      this.running = true;
      post("/api/admin/week-schedule-sync/run", {}).then((res) => {
        if (isSuccess(res)) {
          this.$message.success((res.data.data || {}).message || "检查完成");
          this.loadStatus();
        } else {
          this.$message.error(res.data.msg || "检查失败");
        }
      }).finally(() => {
        this.running = false;
      });
    },
    selectDynamic(row) {
      this.selectedDynamicId = row ? row.id : "";
    },
    manualSync(row) {
      const dynamic = row || {};
      if (!dynamic.id) {
        this.$message.warning("请选择动态");
        return;
      }
      this.selectedDynamicId = dynamic.id;
      this.manualRunning = true;
      post("/api/admin/week-schedule-sync/manual", {
        dynamic_id: dynamic.id,
        account_key: "tianxuanji",
        save_image: 1,
        update_date: 1,
      }).then((res) => {
        if (isSuccess(res)) {
          const data = res.data.data || {};
          this.lastResult = data;
          this.resultVisible = true;
          if (data.success) {
            this.$message.success(data.message || "AI 已写入周表");
          } else {
            this.$message.warning(data.message || "AI 未写入周表");
          }
          this.loadStatus();
        } else {
          this.$message.error(res.data.msg || "AI 执行失败");
        }
      }).finally(() => {
        this.manualRunning = false;
      });
    },
    statusTag(status) {
      return {
        success: "success",
        skipped: "info",
        retry: "warning",
        not_found: "warning",
        not_week_schedule: "info",
        cdn_failed: "warning",
        error: "danger",
      }[status] || "info";
    },
    statusText(status) {
      return {
        success: "成功",
        skipped: "已跳过",
        retry: "待重试",
        not_found: "未找到",
        not_week_schedule: "非周表",
        cdn_failed: "CDN失败",
        error: "错误",
        pending: "等待",
      }[status] || status || "无记录";
    },
    dateStatus(status) {
      const value = Number(status);
      if (value === 1) return "直播";
      if (value === 0) return "休息";
      return "待定";
    },
    typeText(type) {
      const first = String(type || "0").split(",")[0];
      return this.typeMap[Number(first)] || "-";
    },
  },
};
</script>

<style scoped>
.week-page {
  width: 100%;
  height: 100%;
  background: #f5f7fb;
  overflow: auto;
}
.week-card {
  width: 98%;
  margin: 1.5% auto;
  padding: 18px;
  background: #fff;
  box-shadow: #e0e0e0 5px 5px 10px;
  box-sizing: border-box;
}
.toolbar,
.section-head,
.toolbar-actions,
.status-line {
  display: flex;
  align-items: center;
}
.toolbar {
  justify-content: space-between;
  margin-bottom: 14px;
}
.toolbar-actions,
.status-line {
  gap: 10px;
}
.section-head {
  justify-content: space-between;
  margin-bottom: 12px;
}
.title {
  color: #1f2d3d;
  font-size: 22px;
  font-weight: 700;
}
.section {
  border: 1px solid #dfe6ef;
  padding: 14px;
  min-height: 100px;
  box-sizing: border-box;
}
.section-title {
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}
.muted {
  color: #7f8fa4;
  font-size: 13px;
}
.mono {
  font-family: Consolas, Monaco, monospace;
}
.strong {
  color: #1f2d3d;
  font-weight: 700;
  line-height: 1.5;
}
.meta,
.alert,
.bottom-row {
  margin-top: 12px;
}
.link,
.mini-link {
  color: #409eff;
  text-decoration: none;
}
.link {
  display: inline-block;
  margin-top: 10px;
}
.mini-link {
  display: block;
  margin-top: 4px;
  font-size: 12px;
}
.thumb {
  width: 64px;
  height: 48px;
  border: 1px solid #dfe6ef;
}
.result-json {
  max-height: 55vh;
  overflow: auto;
  padding: 12px;
  background: #f7f9fc;
  border: 1px solid #dfe6ef;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
